/** Card-settlement memos (Royal Bank Central Card Centre). */
export const CARD_MEMO_FORMATS = [
  {
    kind: "visa",
    label: "Visa card deposit",
    regex: /^\s*VSA\s*DEP/i,
    example: "VSA DEP13041934",
    exampleLong: "VSA DEP13041934 ROYAL BANK CENTRAL CARD CENTRE",
    count: 1569,
  },
  {
    kind: "mastercard",
    label: "Mastercard card deposit",
    regex: /^\s*MC\s*DEP/i,
    example: "MC DEP 11294238",
    exampleLong: "MC DEP 11294238 ROYAL BANK CENTRAL CARD CENTRE",
    count: 1473,
  },
  {
    kind: "amex",
    label: "Amex card deposit",
    regex: /^\s*AMX\s*DEP/i,
    example: "AMX DEP11294238",
    exampleLong: "AMX DEP11294238 ROYAL BANK CENTRAL CARD CENTRE",
    count: 355,
  },
  {
    kind: "debit",
    // EF<MMDD> <txnId> — e.g. "EF0102 13041934"
    label: "Debit (EF) card deposit",
    regex: /^\s*EF\d{0,4}\b/i,
    example: "EF0102 13041934",
    exampleLong: "EF0325 13041934 ROYAL BANK CENTRAL CARD CENTRE",
    count: 1454,
  },
  {
    kind: "moneris",
    label: "Moneris deposit (MON DEP…)",
    regex: /^\s*MON\s*DEP/i,
    example: "MON DEP12390811",
    exampleLong: "MON DEP12390811 ROYAL BANK CENTRAL CARD CENTRE",
    count: 424,
  },
];

/** Non-card deposit memos (cheques, e-transfers, insurance, claims, etc.) */
export const OTHER_MEMO_FORMATS = [
  {
    kind: "echeque",
    label: "eCheque deposit",
    regex: /^\s*eCheque\s+deposit\b/i,
    example: "eCheque deposit      140367",
    count: 1424,
  },
  {
    kind: "etransfer",
    label: "e-Transfer / E-TRF autodeposit",
    regex: /^\s*(?:e-?transfer|e-?trf)\b/i,
    example: "e-Transfer - Autodeposit Greg Robinson CABX5xJW",
    exampleLong: "E-TRF Autodeposit …",
    count: 418, // 367 (E-TRF) + 51 (e-Transfer)
  },
  {
    kind: "insurance",
    label: "Insurance payor",
    regex: /^\s*insurance\b/i,
    example: "INSURANCE CANADA LIFE",
    examples: [
      "INSURANCE SUN LIFE ASSURANCE COMPANY OF",
      "INSURANCE GREEN SHIELD CANADA INSURANCE",
      "INSURANCE CANADA LIFE",
      "INSURANCE GREEN SHIELD ADJUDICATION INC.",
      "INSURANCE GroupHEALTH",
      "Insurance INSTREAM",
      "Insurance SUNLIFE MED INS",
    ],
    count: 1947,
  },
  {
    kind: "health_dental_claim",
    label: "Health/Dental claim",
    regex: /^\s*health\s*\/\s*dental\s+claim\b/i,
    example: "Health/Dental Claim NexgenRx",
    examples: [
      "HEALTH/DENTAL CLAIM MANULIFE 729642 P26233202",
      "HEALTH/DENTAL CLAIM RWAM INSURANCE ADMINISTRATORS",
      "HEALTH/DENTAL CLAIM NexgenRx Inc.",
      "HEALTH/DENTAL CLAIM CIBC EMPLOYEE DENTAL BENEFIT",
      "Health/Dental Claim Global Trust",
      "Health/Dental Claim OEBAC",
    ],
    count: 1085,
  },
  {
    kind: "misc_payment",
    label: "Misc payment (government / ASO programs)",
    regex: /^\s*misc\s+payment\b/i,
    example: "Misc Payment GC - CDCP - ASO SUNLIFE MED ASO",
    examples: [
      "MISC PAYMENT GVT OF CANADA - CDCP - ASO",
      "MISC PAYMENT SUN LIFE",
      "MISC PAYMENT CROIX BLEUE MEDAVIE BLUE CROSS",
      "MISC PAYMENT ALTA BLUE CROSS",
      "MISC PAYMENT AccertaClaim Servicorp Inc.",
      "MISC PAYMENT ESI CANADA",
      "MISC PAYMENT AMEX BANK OF CANADA PAYMENT",
      "MISC PAYMENT GVT OF CANADA - RCSD - ASO",
      "Misc Payment GC - CDCP - ASO SUNLIFE MED ASO",
      "Misc Payment SUN LIFE SUNLIFE MED ASO",
    ],
    count: 3615,
  },
  {
    // Money moved between our own subsidiaries, not clinic revenue. `\b` keeps
    // "Interac" from matching.
    kind: "intercompany",
    label: "Intercompany transfer",
    regex: /\binter(?:company)?\b/i,
    example: "Intercompany ART to SFC",
    examples: [
      "Inter END to ART",
      "Intercompany  WIN (DESJ) to SFC",
      "Intercompany SFC to LAM (cheque deposit)",
      "Oakville - Intercompany",
    ],
    count: 107, // 2026-01-01 → 2026-10-23
  },
];

/** Combined list, card formats first (matched in this order). */
export const MEMO_FORMATS = [...CARD_MEMO_FORMATS, ...OTHER_MEMO_FORMATS];

/** Canonical card kinds used for matching against the deposit-sheet card columns. */
export const CARD_KINDS = CARD_MEMO_FORMATS.map((f) => f.kind);

/**
 * Classify a memo string into its canonical kind (e.g. "visa", "mastercard",
 * "amex", "debit", "echeque", "insurance", "misc_payment", …).
 * Returns null if no known format matches.
 *
 * @param {string} memo
 * @returns {string|null}
 */
export function classifyMemo(memo) {
  if (!memo) return null;
  for (const fmt of MEMO_FORMATS) {
    if (fmt.regex.test(memo)) return fmt.kind;
  }
  return null;
}

/**
 * True when the memo represents one of the card-settlement formats
 * (visa / mastercard / amex / debit / moneris).
 *
 * @param {string} memo
 * @returns {boolean}
 */
export function isCardMemo(memo) {
  if (!memo) return false;
  return CARD_MEMO_FORMATS.some((f) => f.regex.test(memo));
}

/**
 * True when the memo is an intercompany transfer ("Intercompany ART to SFC",
 * "Inter END to ART").
 *
 * @param {string} memo
 * @returns {boolean}
 */
export function isIntercompanyMemo(memo) {
  return classifyMemo(memo) === "intercompany";
}
